/**
 * Telemetry & Performance Tracking
 * 
 * Track load timings to optimize delays and identify bottlenecks
 */

export interface PerformanceMark {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

export interface TelemetryReport {
  sessionId: string;
  userId?: string;
  marks: PerformanceMark[];
  aggregates: {
    totalLoadTime: number;
    slowestOperation: { name: string; duration: number } | null;
    fastestOperation: { name: string; duration: number } | null;
    averageLoadTime: number;
  };
  timestamp: number;
}

class TelemetryService {
  private marks: Map<string, PerformanceMark> = new Map();
  private completedMarks: PerformanceMark[] = [];
  private sessionId: string;
  private userId?: string;
  private enabled: boolean = true;

  constructor() {
    this.sessionId = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  /**
   * Set user ID for telemetry tracking
   */
  setUserId(userId: string) {
    this.userId = userId;
  }

  /**
   * Enable or disable telemetry
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  /**
   * Start timing an operation
   */
  start(name: string, metadata?: Record<string, any>): void {
    if (!this.enabled) return;

    const mark: PerformanceMark = {
      name,
      startTime: performance.now(),
      metadata
    };

    this.marks.set(name, mark);
    console.debug(`⏱️ [Telemetry] Started: ${name}`, metadata);
  }

  /**
   * End timing an operation
   */
  end(name: string, metadata?: Record<string, any>): number | null {
    if (!this.enabled) return null;

    const mark = this.marks.get(name);
    if (!mark) {
      console.warn(`⚠️ [Telemetry] No start mark found for: ${name}`);
      return null;
    }

    mark.endTime = performance.now();
    mark.duration = mark.endTime - mark.startTime;
    
    if (metadata) {
      mark.metadata = { ...mark.metadata, ...metadata };
    }

    this.completedMarks.push(mark);
    this.marks.delete(name);

    console.debug(`✅ [Telemetry] Completed: ${name} (${Math.round(mark.duration)}ms)`, mark.metadata);

    return mark.duration;
  }

  /**
   * Track an async operation
   */
  async track<T>(
    name: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    if (!this.enabled) {
      return operation();
    }

    this.start(name, metadata);
    
    try {
      const result = await operation();
      this.end(name, { status: 'success' });
      return result;
    } catch (error) {
      this.end(name, { 
        status: 'error', 
        error: error instanceof Error ? error.message : String(error) 
      });
      throw error;
    }
  }

  /**
   * Get all completed marks
   */
  getMarks(): PerformanceMark[] {
    return [...this.completedMarks];
  }

  /**
   * Get marks by name pattern
   */
  getMarksByPattern(pattern: RegExp): PerformanceMark[] {
    return this.completedMarks.filter(mark => pattern.test(mark.name));
  }

  /**
   * Get average duration for marks matching a pattern
   */
  getAverageDuration(pattern: RegExp): number {
    const marks = this.getMarksByPattern(pattern);
    if (marks.length === 0) return 0;

    const total = marks.reduce((sum, mark) => sum + (mark.duration || 0), 0);
    return total / marks.length;
  }

  /**
   * Generate telemetry report
   */
  generateReport(): TelemetryReport {
    const marks = this.getMarks();
    const durations = marks
      .filter(m => m.duration !== undefined)
      .map(m => ({ name: m.name, duration: m.duration! }));

    const totalLoadTime = durations.reduce((sum, d) => sum + d.duration, 0);
    const averageLoadTime = durations.length > 0 ? totalLoadTime / durations.length : 0;

    const slowest = durations.length > 0
      ? durations.reduce((max, d) => d.duration > max.duration ? d : max)
      : null;

    const fastest = durations.length > 0
      ? durations.reduce((min, d) => d.duration < min.duration ? d : min)
      : null;

    return {
      sessionId: this.sessionId,
      userId: this.userId,
      marks,
      aggregates: {
        totalLoadTime,
        slowestOperation: slowest,
        fastestOperation: fastest,
        averageLoadTime
      },
      timestamp: Date.now()
    };
  }

  /**
   * Log report to console
   */
  logReport(): void {
    const report = this.generateReport();
    
    console.group('📊 Telemetry Report');
    console.log('Session ID:', report.sessionId);
    console.log('User ID:', report.userId || 'Anonymous');
    console.log('Total Operations:', report.marks.length);
    console.log('Total Load Time:', Math.round(report.aggregates.totalLoadTime), 'ms');
    console.log('Average Load Time:', Math.round(report.aggregates.averageLoadTime), 'ms');
    
    if (report.aggregates.slowestOperation) {
      console.log('Slowest Operation:', 
        report.aggregates.slowestOperation.name, 
        Math.round(report.aggregates.slowestOperation.duration), 'ms'
      );
    }
    
    if (report.aggregates.fastestOperation) {
      console.log('Fastest Operation:', 
        report.aggregates.fastestOperation.name, 
        Math.round(report.aggregates.fastestOperation.duration), 'ms'
      );
    }
    
    console.table(
      report.marks.map(m => ({
        Operation: m.name,
        Duration: m.duration ? `${Math.round(m.duration)}ms` : 'N/A',
        Status: m.metadata?.status || 'N/A'
      }))
    );
    console.groupEnd();
  }

  /**
   * Clear all marks
   */
  clear(): void {
    this.marks.clear();
    this.completedMarks = [];
  }

  /**
   * Export report as JSON
   */
  exportReport(): string {
    return JSON.stringify(this.generateReport(), null, 2);
  }

  /**
   * Send report to analytics endpoint (optional)
   */
  async sendReport(endpoint: string): Promise<void> {
    try {
      const report = this.generateReport();
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(report)
      });
      console.log('📤 Telemetry report sent successfully');
    } catch (error) {
      console.error('❌ Failed to send telemetry report:', error);
    }
  }
}

// Singleton instance
export const telemetry = new TelemetryService();

// Helper functions for common operations
export const telemetryHelpers = {
  /**
   * Track page load
   */
  trackPageLoad: (pageName: string) => {
    telemetry.start(`page:${pageName}:load`);
  },

  /**
   * Complete page load tracking
   */
  completePageLoad: (pageName: string) => {
    const duration = telemetry.end(`page:${pageName}:load`);
    if (duration && duration > 3000) {
      console.warn(`⚠️ Slow page load detected: ${pageName} took ${Math.round(duration)}ms`);
    }
    return duration;
  },

  /**
   * Track data fetch
   */
  trackDataFetch: async <T>(
    dataType: string,
    fetchFn: () => Promise<T>
  ): Promise<T> => {
    return telemetry.track(`data:${dataType}:fetch`, fetchFn);
  },

  /**
   * Track component render
   */
  trackComponentRender: (componentName: string) => {
    telemetry.start(`component:${componentName}:render`);
  },

  /**
   * Complete component render tracking
   */
  completeComponentRender: (componentName: string) => {
    return telemetry.end(`component:${componentName}:render`);
  }
};

export default telemetry;
